import { useEffect, useState } from "react";
import { MapPin, ExternalLink } from "lucide-react";
import { COMPANY } from "@/data/site";
import { isAllowed, setConsent, getConsent } from "@/lib/consent";

const ADDRESS = `${COMPANY.address.street}, ${COMPANY.address.zip} ${COMPANY.address.city}`;
const MAPS_LINK = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS)}`;
// Lightweight embed (no API key required) — only loads after user consent
const EMBED_SRC = `https://maps.google.com/maps?q=${encodeURIComponent(ADDRESS)}&output=embed`;

export default function MapsEmbed() {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    setAllowed(isAllowed("maps"));
    const onChange = () => setAllowed(isAllowed("maps"));
    window.addEventListener("consent-change", onChange);
    return () => window.removeEventListener("consent-change", onChange);
  }, []);

  const enable = () => {
    const existing = getConsent() || {};
    setConsent({ ...existing, maps: true });
  };

  return (
    <div data-testid="maps-embed" className="mt-10 border border-ink-500/60 overflow-hidden bg-ink-700">
      <div className="flex items-center justify-between px-5 py-3 border-b border-ink-500/60">
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-gold" />
          <p className="text-xs uppercase tracking-[0.2em] text-gold">Standort</p>
        </div>
        <a
          data-testid="maps-open-link"
          href={MAPS_LINK}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-gold transition-colors"
        >
          In Google Maps öffnen <ExternalLink size={12} />
        </a>
      </div>

      {allowed ? (
        <iframe
          data-testid="maps-iframe"
          title={`Karte – ${COMPANY.name}`}
          src={EMBED_SRC}
          width="100%"
          height="340"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          style={{ border: 0, filter: "grayscale(0.4) contrast(1.05) brightness(0.92)" }}
        />
      ) : (
        <div
          data-testid="maps-placeholder"
          className="relative h-[340px] bg-[radial-gradient(circle_at_30%_30%,#1E1E24,#0B0B0C)] flex items-center justify-center px-6"
        >
          <div className="text-center max-w-md">
            <MapPin size={32} className="text-gold mx-auto mb-4" />
            <p className="text-white font-display text-xl mb-2">Karte deaktiviert</p>
            <p className="text-sm text-zinc-400 mb-5 leading-relaxed">
              Die Standort-Karte wird von Google Maps bereitgestellt. Mit Aktivierung stimmen Sie
              der Datenübertragung an Google zu. Sie können die Einwilligung jederzeit widerrufen.
            </p>
            <button
              data-testid="maps-enable"
              onClick={enable}
              className="px-6 py-3 bg-gold text-ink-900 text-xs font-medium uppercase tracking-wider hover:bg-gold-hover transition-colors"
            >
              Karte aktivieren
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
